import { useState, useEffect, useRef } from 'react';
import StatPickerModal from '../../components/analytics/StatPickerModal';
import '../../components/analytics/StatPickerModal.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import api, { bragCardAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import BragCardPreview from '../../components/tracker/BragCardPreview';
import InlineCropper from '../../components/InlineCropper';
import { useBackgroundRemoval } from '../../hooks/useBackgroundRemoval';
import RotationPrompt from '../../components/create-graphic/RotationPrompt';
import '../CreateGraphic.css';
import './BragCardPage.css';

const UNSPLASH_KEY = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;

const MILESTONE_DEFAULTS = {
  hr: { label: 'FIRST CAREER HR!', emoji: '💣' },
  multihit: { label: 'MULTI-HIT GAME!', emoji: '🔥' },
  strikeouts: { label: 'DOMINANT OUTING!', emoji: '🔥' },
  hot_avg: { label: '.400+ SEASON AVG!', emoji: '📈' },
};

export default function BragCardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const cardId = searchParams.get('cardId');
  const [savedCardId, setSavedCardId] = useState(null);
  const [games, setGames] = useState([]);
  const [showStatPicker, setShowStatPicker] = useState(false);
  const [gameContext, setGameContext] = useState('');
  const milestoneType = searchParams.get('milestone') || 'multihit';
  const hasExplicitMilestone = searchParams.get('milestone') !== null;
  const playerId = searchParams.get('playerId');
  const urlPlayerName = searchParams.get('highlightPlayer') || '';
  const urlStatLine = searchParams.get('highlightStats') || '';

  const defaults =
    MILESTONE_DEFAULTS[milestoneType] || MILESTONE_DEFAULTS.multihit;

  const [playerName, setPlayerName] = useState(urlPlayerName);
  const [statLine, setStatLine] = useState(urlStatLine);
  const [milestoneLabel, setMilestoneLabel] = useState(hasExplicitMilestone ? defaults.label : '');
  const [milestoneEmoji, setMilestoneEmoji] = useState(hasExplicitMilestone ? defaults.emoji : '');
  const [primaryColor, setPrimaryColor] = useState('#1a1e2e');
  const [accentColor, setAccentColor] = useState('#e8c84a');
  const [playerPhoto, setPlayerPhoto] = useState(null);
  const [playerOriginalImage, setPlayerOriginalImage] = useState(null);
  const [cutoutPlayerPhoto, setCutoutPlayerPhoto] = useState(null);
  const [croppedPlayerPhoto, setCroppedPlayerPhoto] = useState(null);
  const [playerPhotoX, setPlayerPhotoX] = useState(50);
  const [playerPhotoY, setPlayerPhotoY] = useState(50);
  const [playerPhotoScale, setPlayerPhotoScale] = useState(100);
  const [activePlayerPhotoVersion, setActivePlayerPhotoVersion] = useState('original');

  const { removeBg, isRemoving, bgRemovalError } = useBackgroundRemoval();

  const canUseCutouts = ['pro', 'premium', 'organization'].includes(user?.subscriptionPlan);

  const activePlayerPhoto = activePlayerPhotoVersion === 'cutout' && cutoutPlayerPhoto
    ? cutoutPlayerPhoto
    : playerPhoto;
  const [playerTempImage, setPlayerTempImage] = useState(null);
  const [playerCropping, setPlayerCropping] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [bgMode, setBgMode] = useState('pexels');
  const [bgOriginalImage, setBgOriginalImage] = useState(null);
  const [bgTempImage, setBgTempImage] = useState(null);
  const [bgCropping, setBgCropping] = useState(false);

  const [pexelsQuery, setPexelsQuery] = useState('baseball stadium');
  const [pexelsResults, setPexelsResults] = useState([]);
  const [pexelsLoading, setPexelsLoading] = useState(true);
  const [pexelsLoadingMore, setPexelsLoadingMore] = useState(false);
  const [pexelsPage, setPexelsPage] = useState(1);
  const [pexelsTotalResults, setPexelsTotalResults] = useState(0);

  const [templateStyle, setTemplateStyle] = useState('classic');

  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveLabel, setSaveLabel] = useState('Save');
  const [showRotationPrompt, setShowRotationPrompt] = useState(false);
  const [rotationDismissed, setRotationDismissed] = useState(false);

  const playerPhotoContainerRef = useRef(null);

  // Rotation prompt — fires when the preview panel scrolls into view on mobile portrait
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth < 768 && window.innerHeight > window.innerWidth) {
        const previewSection = document.querySelector('#showRotate');
        if (previewSection && !rotationDismissed) {
          const rect = previewSection.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            setShowRotationPrompt(true);
          } else {
            setShowRotationPrompt(false);
          }
        }
      } else {
        setShowRotationPrompt(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [rotationDismissed]);

  // Auto-search Pexels on mount
  useEffect(() => {
    searchPexels('baseball stadium');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load a saved brag card when ?cardId= is present
  useEffect(() => {
    if (!cardId) return;
    bragCardAPI.getById(cardId)
      .then((res) => {
        const c = res.data;
        setPlayerName(c.playerName || '');
        setStatLine(c.statLine || '');
        setMilestoneLabel(c.milestoneLabel || '');
        setMilestoneEmoji(c.milestoneEmoji || defaults.emoji);
        setPrimaryColor(c.primaryColor || '#1a1e2e');
        setAccentColor(c.accentColor || '#e8c84a');
        if (c.playerPhoto) {
          setPlayerOriginalImage(c.playerPhoto);
          if (c.playerPhotoCutout) setCutoutPlayerPhoto(c.playerPhotoCutout);
          if (c.playerPhotoCropped) setCroppedPlayerPhoto(c.playerPhotoCropped);
          if (c.playerPhotoVersion) setActivePlayerPhotoVersion(c.playerPhotoVersion);
          if (c.playerPhotoVersion === 'cutout' && c.playerPhotoCutout) {
            setPlayerPhoto(c.playerPhotoCutout);
          } else if (c.playerPhotoCropped) {
            setPlayerPhoto(c.playerPhotoCropped);
          } else {
            setPlayerPhoto(c.playerPhoto);
          }
        }
        if (c.templateStyle) setTemplateStyle(c.templateStyle);
        if (c.backgroundImage) setBackgroundImage(c.backgroundImage);
        if (c.playerPhotoX != null) setPlayerPhotoX(c.playerPhotoX);
        if (c.playerPhotoY != null) setPlayerPhotoY(c.playerPhotoY);
        if (c.playerPhotoScale != null) setPlayerPhotoScale(c.playerPhotoScale);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId]);

  // Fetch player name from API if only playerId was passed (no name in URL)
  useEffect(() => {
    if (!playerId || urlPlayerName) return;
    api
      .get(`/analytics/players/${playerId}`)
      .then((res) => {
        const p = res.data;
        if (p) setPlayerName(`${p.firstName} ${p.lastName}`.toUpperCase());
      })
      .catch(() => {});
  }, [playerId, urlPlayerName]);

  // Fetch games for stat picker
  useEffect(() => {
    if (!playerId) return;
    api
      .get(`/analytics/players/${playerId}/games`)
      .then((res) => setGames(res.data || []))
      .catch(() => {});
  }, [playerId]);

  async function handleSave() {
    setSaving(true);
    setSaveLabel('Saving…');
    try {
      // Capture a lightweight thumbnail using the same clone logic as handleDownload
      let thumbnailImage = null;
      try {
        const originalElement = document.getElementById('brag-card-preview');
        if (originalElement) {
          let photoContainerW = 0;
          let photoContainerH = 0;
          if (playerPhoto && playerPhotoContainerRef.current) {
            photoContainerW = playerPhotoContainerRef.current.offsetWidth;
            photoContainerH = playerPhotoContainerRef.current.offsetHeight;
          }

          const tempContainer = document.createElement('div');
          tempContainer.style.position = 'fixed';
          tempContainer.style.left = '-9999px';
          tempContainer.style.top = '0';
          tempContainer.style.width = '1080px';
          tempContainer.style.zIndex = '-1';
          document.body.appendChild(tempContainer);

          const cloned = originalElement.cloneNode(true);
          cloned.style.width = '1080px';
          cloned.style.height = '1350px';
          cloned.style.transform = 'none';
          tempContainer.appendChild(cloned);

          if (playerPhoto && photoContainerW && photoContainerH) {
            const clonedContainer = cloned.querySelector(
              '.brag-card-player-photo-container'
            );
            if (clonedContainer) {
              const containerLeft = Math.round((1080 - photoContainerW) / 2);
              clonedContainer.style.left = `${containerLeft}px`;
              clonedContainer.style.transform = 'none';
              clonedContainer.style.width = `${photoContainerW}px`;
              clonedContainer.style.height = `${photoContainerH}px`;
              const clonedImg = clonedContainer.querySelector('img');
              if (clonedImg) {
                const imgW = photoContainerW - 28;
                const imgH = photoContainerH - 28;
                clonedImg.style.display = 'block';
                clonedImg.style.width = `${imgW}px`;
                clonedImg.style.height = `${imgH}px`;
                clonedImg.style.maxWidth = 'none';
                clonedImg.style.maxHeight = 'none';
                clonedImg.style.minWidth = '0';
              }
            }
          }

          await new Promise((r) => setTimeout(r, 500));

          const canvas = await html2canvas(cloned, {
            scale: 0.25,
            backgroundColor: null,
            logging: false,
            useCORS: true,
            allowTaint: true,
            width: 1080,
            height: 1350,
            imageTimeout: 0,
            removeContainer: true,
            windowWidth: 1080,
            windowHeight: 1350,
          });

          document.body.removeChild(tempContainer);
          thumbnailImage = canvas.toDataURL('image/jpeg', 0.75);
        }
      } catch (thumbErr) {
        console.warn('Thumbnail capture failed (non-fatal):', thumbErr);
      }

      const payload = {
        playerId: playerId || null,
        playerName,
        statLine,
        milestoneLabel,
        milestoneEmoji,
        milestoneType,
        primaryColor,
        accentColor,
        playerPhoto: playerOriginalImage,
        playerPhotoCutout: cutoutPlayerPhoto || null,
        playerPhotoCropped: croppedPlayerPhoto || null,
        playerPhotoX,
        playerPhotoY,
        playerPhotoScale,
        backgroundImage: backgroundImage || null,
        thumbnailImage,
        templateStyle,
      };

      const existingId = savedCardId || cardId;
      let res;
      if (existingId) {
        res = await bragCardAPI.update(existingId, payload);
      } else {
        res = await bragCardAPI.save(payload);
        if (res?.data?.id) setSavedCardId(res.data.id);
      }
      setSaveLabel('Saved ✓');
      setTimeout(() => setSaveLabel('Save'), 2500);
    } catch (err) {
      console.error('Save failed:', err);
      if (err?.response?.data?.limitReached) {
        setSaveLabel('Limit reached');
        alert(err.response.data.message || 'You have reached your BragCard limit. Upgrade to create more.');
      } else {
        setSaveLabel('Save failed');
      }
      setTimeout(() => setSaveLabel('Save'), 2500);
    } finally {
      setSaving(false);
    }
  }

  function handlePhotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setPlayerOriginalImage(reader.result);
      setPlayerTempImage(reader.result);
      setPlayerCropping(true);
    };
    reader.readAsDataURL(file);
  }

  function handleEditPlayerCrop() {
    setPlayerTempImage(playerOriginalImage);
    setPlayerCropping(true);
  }

  function handlePlayerCropComplete(croppedImage) {
    setPlayerPhoto(croppedImage);
    setCroppedPlayerPhoto(croppedImage);
    setPlayerCropping(false);
    setPlayerTempImage(null);
    const input = document.getElementById('brag-card-player-photo');
    if (input) input.value = '';
  }

  function handlePlayerCropCancel() {
    setPlayerCropping(false);
    setPlayerTempImage(null);
    const input = document.getElementById('brag-card-player-photo');
    if (input) input.value = '';
  }

  function handleRemovePlayerPhoto() {
    setPlayerPhoto(null);
    setPlayerOriginalImage(null);
    setCroppedPlayerPhoto(null);
    setCutoutPlayerPhoto(null);
    setPlayerPhotoX(50);
    setPlayerPhotoY(50);
    setPlayerPhotoScale(100);
  }

  const handleRemoveBg = async () => {
    const sourceImage = croppedPlayerPhoto || playerPhoto || playerOriginalImage;
    if (!sourceImage) return;
    const result = await removeBg(sourceImage);
    if (result) {
      const { blobUrl, toBase64 } = result;
      setCutoutPlayerPhoto(blobUrl);
      setActivePlayerPhotoVersion('cutout');
      setPlayerPhoto(blobUrl);
      toBase64.then((base64) => {
        setCutoutPlayerPhoto(base64);
        setPlayerPhoto(base64);
      });
    }
  };

  const handleTogglePhotoVersion = (version) => {
    setActivePlayerPhotoVersion(version);
    setPlayerPhoto(version === 'cutout' ? cutoutPlayerPhoto : (croppedPlayerPhoto || playerOriginalImage));
  };

  function handleBgPhotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setBgOriginalImage(reader.result);
      setBgTempImage(reader.result);
      setBgCropping(true);
    };
    reader.readAsDataURL(file);
  }

  function handleEditBgCrop() {
    setBgTempImage(bgOriginalImage);
    setBgCropping(true);
  }

  function handleBgCropComplete(croppedImage) {
    setBackgroundImage(croppedImage);
    setBgCropping(false);
    setBgTempImage(null);
    const input = document.getElementById('brag-card-bg-photo');
    if (input) input.value = '';
  }

  function handleBgCropCancel() {
    setBgCropping(false);
    setBgTempImage(null);
    const input = document.getElementById('brag-card-bg-photo');
    if (input) input.value = '';
  }

  function handleRemoveBgPhoto() {
    setBackgroundImage(null);
    setBgOriginalImage(null);
  }

  async function searchPexels(query, page = 1, append = false) {
    if (append) {
      setPexelsLoadingMore(true);
    } else {
      setPexelsLoading(true);
    }
    try {
      const res = await api.get('/pexels/search', {
        params: { query, page, per_page: 12 },
      });
      const photos = res.data.photos || [];
      setPexelsTotalResults(res.data.total_results || 0);
      setPexelsPage(page);
      if (append) {
        setPexelsResults((prev) => {
          const combined = [...prev, ...photos];
          return combined.filter(
            (photo, index, self) =>
              index === self.findIndex((p) => p.id === photo.id)
          );
        });
      } else {
        setPexelsResults(photos);
      }
    } catch (err) {
      console.error('Pexels search failed:', err);
      if (!append) setPexelsResults([]);
    } finally {
      if (append) {
        setPexelsLoadingMore(false);
      } else {
        setPexelsLoading(false);
      }
    }
  }

  function handlePexelsSearch(e) {
    e.preventDefault();
    searchPexels(pexelsQuery, 1, false);
  }

  function handlePexelsLoadMore() {
    searchPexels(pexelsQuery, pexelsPage + 1, true);
  }

  async function handleDownload() {
    try { await handleSave(); } catch (_) {}
    setDownloading(true);
    try {
      const originalElement = document.getElementById('brag-card-preview');
      if (!originalElement) throw new Error('Preview element not found');

      // Measure Classic photo container before cloning (percentage-based centering
      // must be replaced with explicit px so html2canvas renders it correctly).
      let photoContainerW = 0;
      let photoContainerH = 0;
      if (playerPhoto && playerPhotoContainerRef.current) {
        photoContainerW = playerPhotoContainerRef.current.offsetWidth;
        photoContainerH = playerPhotoContainerRef.current.offsetHeight;
      }

      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'fixed';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '1080px';
      tempContainer.style.zIndex = '-1';
      document.body.appendChild(tempContainer);

      const cloned = originalElement.cloneNode(true);
      cloned.style.width = '1080px';
      cloned.style.height = '1350px';
      cloned.style.transform = 'none';
      tempContainer.appendChild(cloned);

      // ── Classic template: fix left:'50%' + transform centering on photo container ──
      if (playerPhoto && photoContainerW && photoContainerH) {
        const clonedContainer = cloned.querySelector(
          '.brag-card-player-photo-container'
        );
        if (clonedContainer) {
          const containerLeft = Math.round((1080 - photoContainerW) / 2);
          clonedContainer.style.left = `${containerLeft}px`;
          clonedContainer.style.transform = 'none';
          clonedContainer.style.width = `${photoContainerW}px`;
          clonedContainer.style.height = `${photoContainerH}px`;
          const clonedImg = clonedContainer.querySelector('img');
          if (clonedImg) {
            // offsetWidth includes the 14px border on each side (28px total)
            const imgW = photoContainerW - 28;
            const imgH = photoContainerH - 28;
            clonedImg.style.display = 'block';
            clonedImg.style.width = `${imgW}px`;
            clonedImg.style.height = `${imgH}px`;
            clonedImg.style.maxWidth = 'none';
            clonedImg.style.maxHeight = 'none';
            clonedImg.style.minWidth = '0';
          }
        }
      }

      // ── Preload all images in the clone before html2canvas fires ──────────
      // Cloned nodes at -9999px may not be loaded yet. Explicitly trigger each
      // src and wait for completion so html2canvas sees loaded pixels, not blank.
      const clonedImgs = Array.from(cloned.querySelectorAll('img'));
      await Promise.all(
        clonedImgs.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) {
                resolve();
                return;
              }
              img.onload = resolve;
              img.onerror = resolve; // don't block the export on a failed image
              const src = img.getAttribute('src');
              if (src) {
                img.crossOrigin = 'anonymous';
                img.src = '';
                img.src = src;
              } else {
                resolve();
              }
            })
        )
      );

      // ── Player Card template: fix top:'50%'/left:'50%'/translate(-50%,-50%)
      //    on the photo img — compute exact px offsets from the loaded image dims ──
      if (templateStyle === 'player-card' && playerPhoto) {
        const pcContainer = cloned.querySelector('.brag-card-pc-photo-container');
        if (pcContainer) {
          const pcImg = pcContainer.querySelector('img');
          if (pcImg) {
            // Inner content area is 1008×572 (1016×580 container - 4px border each side).
            // Width is locked to 1008px; height scales from natural aspect ratio.
            const displayedW = 1008;
            const displayedH =
              pcImg.naturalWidth > 0
                ? Math.max(572, Math.round((pcImg.naturalHeight * displayedW) / pcImg.naturalWidth))
                : 572;
            const topOffset = -Math.round((displayedH - 572) / 2);
            pcImg.style.position = 'absolute';
            pcImg.style.top = `${topOffset}px`;
            pcImg.style.left = '0px';
            pcImg.style.transform = 'none';
            pcImg.style.width = `${displayedW}px`;
            pcImg.style.height = `${displayedH}px`;
            pcImg.style.maxWidth = 'none';
            pcImg.style.minWidth = '0';
            pcImg.style.minHeight = '0';
          }
        }
      }

      await new Promise((r) => setTimeout(r, 500));

      const canvas = await html2canvas(cloned, {
        scale: 1,
        backgroundColor: null,
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: 1080,
        height: 1350,
        imageTimeout: 0,
        removeContainer: true,
        windowWidth: 1080,
        windowHeight: 1350,
      });

      document.body.removeChild(tempContainer);

      canvas.toBlob(
        (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const safeName = (playerName || 'player')
            .replace(/\s+/g, '-')
            .toLowerCase();
          link.download = `${safeName}-brag-card-${Date.now()}.png`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
          setDownloading(false);
        },
        'image/png',
        1.0
      );
    } catch (err) {
      console.error('Brag card download failed:', err);
      setDownloading(false);
    }
  }

  return (
    <>
      <div className="create-graphic-container">
        {showRotationPrompt && !rotationDismissed && (
          <RotationPrompt
            onDismiss={() => {
              setShowRotationPrompt(false);
              setRotationDismissed(true);
            }}
          />
        )}

        <div className="graphic-builder">
          {/* ── FORM PANEL ───────────────────── */}
          <div className="builder-form">
            <button className="btn-back-dashboard" onClick={() => navigate(-1)}>
              ← Back
            </button>

            <h2 className="brag-card-form-title">Brag Card</h2>

            {/* ── Template selector ─────────────────── */}
            <div className="brag-card-template-selector">
              <button
                type="button"
                className={`brag-card-template-btn${templateStyle === 'classic' ? ' active' : ''}`}
                onClick={() => setTemplateStyle('classic')}
              >
                <div className="bct-thumb bct-classic-thumb">
                  <div className="bct-t-banner" />
                  <div className="bct-t-photo" />
                  <div className="bct-t-stats" />
                </div>
                <span>Classic</span>
              </button>
              <button
                type="button"
                className={`brag-card-template-btn${templateStyle === 'player-card' ? ' active' : ''}`}
                onClick={() => setTemplateStyle('player-card')}
              >
                <div className="bct-thumb bct-player-card-thumb">
                  <div className="bct-t-name" />
                  <div className="bct-t-photo-full" />
                  <div className="bct-t-stat-lines">
                    <div /><div /><div />
                  </div>
                </div>
                <span>Player Card</span>
              </button>
            </div>

            <div className="graphic-form">
              <div className="form-group">
                <label>Player Name</label>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value.toUpperCase())}
                  placeholder="PLAYER NAME"
                />
              </div>

              <div className="form-group">
                <label>
                  Stat Line
                  <button
                    type="button"
                    className="spm-import-btn"
                    onClick={() => setShowStatPicker(true)}
                  >
                    Import Stats →
                  </button>
                </label>
                <input
                  type="text"
                  value={statLine}
                  onChange={(e) => setStatLine(e.target.value)}
                  placeholder="2/4 · 1 HR · 3 RBI"
                />
              </div>

              <div className="form-group">
                <label>Milestone Label</label>
                <input
                  type="text"
                  value={milestoneLabel}
                  onChange={(e) =>
                    setMilestoneLabel(e.target.value.toUpperCase())
                  }
                  placeholder="FIRST CAREER HR!"
                />
              </div>

              {templateStyle !== 'player-card' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Primary Color</label>
                    <div className="brag-card-color-row">
                      <input
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="brag-card-color-input"
                      />
                      <span className="brag-card-color-hex">{primaryColor}</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Accent Color</label>
                    <div className="brag-card-color-row">
                      <input
                        type="color"
                        value={accentColor}
                        onChange={(e) => setAccentColor(e.target.value)}
                        className="brag-card-color-input"
                      />
                      <span className="brag-card-color-hex">{accentColor}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>
                  Player Photo{' '}
                  <span
                    className="form-helper-text"
                    style={{ textTransform: 'none', fontWeight: 400 }}
                  >
                    (optional)
                  </span>
                </label>
                {playerCropping ? (
                  <InlineCropper
                    imageSrc={playerTempImage}
                    onCropComplete={handlePlayerCropComplete}
                    onCancel={handlePlayerCropCancel}
                    aspectRatio={templateStyle === 'player-card' ? 4 / 3 : undefined}
                  />
                ) : playerPhoto ? (
                  <div>
                    <div className="dominant-image-preview-container">
                      <img
                        src={activePlayerPhoto}
                        alt="Player"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {isRemoving && (
                        <div style={{
                          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10,
                        }}>
                          <div style={{
                            width: '36px', height: '36px',
                            border: '3px solid rgba(255,255,255,0.3)',
                            borderTopColor: '#fff', borderRadius: '50%',
                            animation: 'bg-removal-spin 0.75s linear infinite',
                          }} />
                          <p style={{ color: '#fff', fontSize: '13px', margin: '10px 0 0' }}>
                            Removing background…
                          </p>
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn-edit-photo-overlay"
                        onClick={handleEditPlayerCrop}
                        disabled={isRemoving}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-remove-background-photo"
                        onClick={handleRemovePlayerPhoto}
                        disabled={isRemoving}
                      >
                        ✕ Remove
                      </button>
                    </div>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={canUseCutouts ? handleRemoveBg : undefined}
                      disabled={!canUseCutouts || isRemoving}
                      style={
                        !canUseCutouts
                          ? { opacity: 0.5, cursor: 'not-allowed', marginTop: '8px', width: '100%' }
                          : { marginTop: '8px', width: '100%' }
                      }
                    >
                      {isRemoving
                        ? 'Removing background…'
                        : canUseCutouts
                          ? '✂️ Remove Background'
                          : '🔒 Remove Background — Pro feature'}
                    </button>
                    {bgRemovalError && (
                      <p style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>
                        {bgRemovalError}
                      </p>
                    )}
                    {playerOriginalImage && cutoutPlayerPhoto && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button
                          type="button"
                          className={activePlayerPhotoVersion === 'original' ? 'btn-primary' : 'btn-secondary'}
                          onClick={() => handleTogglePhotoVersion('original')}
                          style={{ flex: 1 }}
                          disabled={isRemoving}
                        >
                          Original
                        </button>
                        <button
                          type="button"
                          className={activePlayerPhotoVersion === 'cutout' ? 'btn-primary' : 'btn-secondary'}
                          onClick={() => handleTogglePhotoVersion('cutout')}
                          style={{ flex: 1 }}
                          disabled={isRemoving}
                        >
                          ✂️ Cutout
                        </button>
                      </div>
                    )}
                    {playerPhoto && (
                      <div style={{ marginTop: '12px' }}>
                        <div className="form-section-label" style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: '#8a95a8', marginBottom: '10px' }}>
                          Image Controls
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ fontSize: '12px', fontWeight: 600, color: '#4a5568', display: 'flex', justifyContent: 'space-between' }}>
                            <span>Image Position — Left / Right</span>
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                            <span style={{ fontSize: '11px', color: '#8a95a8' }}>Left</span>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              step={5}
                              value={playerPhotoX}
                              onChange={e => setPlayerPhotoX(Number(e.target.value))}
                              style={{ flex: 1 }}
                            />
                            <span style={{ fontSize: '11px', color: '#8a95a8' }}>Right</span>
                          </div>
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
                            Image Position — Up / Down
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                            <span style={{ fontSize: '11px', color: '#8a95a8' }}>Up</span>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              step={5}
                              value={playerPhotoY}
                              onChange={e => setPlayerPhotoY(Number(e.target.value))}
                              style={{ flex: 1 }}
                            />
                            <span style={{ fontSize: '11px', color: '#8a95a8' }}>Down</span>
                          </div>
                        </div>
                        <div style={{ marginBottom: '4px' }}>
                          <label style={{ fontSize: '12px', fontWeight: 600, color: '#4a5568', display: 'flex', justifyContent: 'space-between' }}>
                            <span>Image Size</span>
                            <span style={{ color: '#8a95a8' }}>({playerPhotoScale}%)</span>
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                            <span style={{ fontSize: '11px', color: '#8a95a8' }}>Smaller</span>
                            <input
                              type="range"
                              min={40}
                              max={200}
                              step={5}
                              value={playerPhotoScale}
                              onChange={e => setPlayerPhotoScale(Number(e.target.value))}
                              style={{ flex: 1 }}
                            />
                            <span style={{ fontSize: '11px', color: '#8a95a8' }}>Larger</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <label className="background-photo-upload-btn">
                    <input
                      id="brag-card-player-photo"
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }}
                    />
                    <span>+ Upload Photo</span>
                  </label>
                )}
              </div>

              <div className="brag-card-section-title">Background</div>

              {/* ── Background mode tabs ─────────────────────── */}
              <div className="form-group">
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  <button
                    type="button"
                    className={bgMode === 'pexels' ? 'btn-primary' : 'btn-secondary'}
                    style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
                    onClick={() => { setBgMode('pexels'); if (!pexelsResults.length) searchPexels(pexelsQuery, 1, false); }}
                  >
                    Browse Free
                  </button>
                  <button
                    type="button"
                    className={bgMode === 'upload' ? 'btn-primary' : 'btn-secondary'}
                    style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
                    onClick={() => setBgMode('upload')}
                  >
                    Upload Photo
                  </button>
                  <button
                    type="button"
                    className={bgMode === 'none' ? 'btn-primary' : 'btn-secondary'}
                    style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
                    onClick={() => { setBgMode('none'); setBackgroundImage(null); }}
                  >
                    No Background
                  </button>
                </div>

                {/* ── Browse Free (Pexels) ── */}
                {bgMode === 'pexels' && (
                  <>
                    <form onSubmit={handlePexelsSearch} className="brag-card-unsplash-form">
                      <input
                        type="text"
                        value={pexelsQuery}
                        onChange={(e) => setPexelsQuery(e.target.value)}
                        placeholder="baseball stadium, field…"
                      />
                      <button type="submit" className="brag-card-search-btn" disabled={pexelsLoading}>
                        {pexelsLoading ? '…' : 'Search'}
                      </button>
                    </form>

                    {backgroundImage?.includes('pexels.com') && (
                      <div className="brag-card-unsplash-active">
                        <img src={backgroundImage} alt="Selected background" crossOrigin="anonymous" />
                        <button type="button" className="brag-card-remove-btn" onClick={() => setBackgroundImage(null)}>
                          Remove
                        </button>
                      </div>
                    )}

                    {!pexelsLoading && pexelsResults.length === 0 && (
                      <p className="brag-card-pexels-empty">No photos found.</p>
                    )}

                    {pexelsResults.length > 0 && (
                      <div className="brag-card-unsplash-grid">
                        {pexelsResults.map((photo) => (
                          <button
                            key={photo.id}
                            className={`brag-card-unsplash-thumb${backgroundImage === photo.src.large ? ' selected' : ''}`}
                            onClick={() => setBackgroundImage(photo.src.large)}
                            title={photo.photographer || ''}
                            type="button"
                          >
                            <img src={photo.src.medium} alt={photo.alt || ''} crossOrigin="anonymous" />
                          </button>
                        ))}
                      </div>
                    )}

                    {pexelsResults.length > 0 && pexelsResults.length < pexelsTotalResults && (
                      <button
                        type="button"
                        className="brag-card-search-btn brag-card-load-more-btn"
                        onClick={handlePexelsLoadMore}
                        disabled={pexelsLoadingMore}
                      >
                        {pexelsLoadingMore ? 'Loading…' : 'Load more'}
                      </button>
                    )}

                    <p className="brag-card-pexels-credit">
                      Photos: <a href="https://www.pexels.com" target="_blank" rel="noreferrer">Pexels</a>
                    </p>
                  </>
                )}

                {/* ── Upload Photo ── */}
                {bgMode === 'upload' && (
                  <>
                    {bgCropping ? (
                      <InlineCropper
                        imageSrc={bgTempImage}
                        onCropComplete={handleBgCropComplete}
                        onCancel={handleBgCropCancel}
                      />
                    ) : backgroundImage && !backgroundImage.startsWith('http') ? (
                      <div className="brag-card-photo-preview">
                        <img src={backgroundImage} alt="Background" />
                        <button type="button" className="brag-card-edit-crop-btn" onClick={handleEditBgCrop}>
                          Edit Crop
                        </button>
                        <button type="button" className="brag-card-remove-btn" onClick={handleRemoveBgPhoto}>
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="background-photo-upload-btn">
                        <input
                          id="brag-card-bg-photo"
                          type="file"
                          accept="image/jpeg,image/png"
                          onChange={handleBgPhotoChange}
                          style={{ display: 'none' }}
                        />
                        <span>+ Upload Background</span>
                      </label>
                    )}
                  </>
                )}

                {/* ── No Background ── */}
                {bgMode === 'none' && (
                  <p style={{ fontSize: '13px', color: '#8a95a8', marginTop: '8px' }}>
                    No background image — the card will use its default design.
                  </p>
                )}
              </div>

              <div className="form-actions-save">
                <button
                  className="btn-save-card"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saveLabel}
                </button>
                <button
                  className="btn-download"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading ? 'Downloading…' : '⬇ Download'}
                </button>
              </div>
            </div>
          </div>

          {/* ── PREVIEW PANEL ────────────────── */}
          <div className="builder-preview">
            <div className="preview-header">
              <h3>Live Preview</h3>
              <p>1080 × 1350 px · Portrait ready</p>
            </div>
            <div className="preview-container">
              <span id="showRotate"></span>
              <div className="brag-card-preview-wrapper">
                <div className="brag-card-preview-inner">
                  <BragCardPreview
                    playerName={playerName}
                    statLine={statLine}
                    milestoneLabel={milestoneLabel}
                    milestoneEmoji={milestoneEmoji}
                    milestoneType={milestoneType}
                    primaryColor={primaryColor}
                    accentColor={accentColor}
                    playerPhoto={activePlayerPhoto}
                    backgroundImage={backgroundImage}
                    subscriptionPlan={user?.subscriptionPlan}
                    playerPhotoContainerRef={playerPhotoContainerRef}
                    playerPhotoX={playerPhotoX}
                    playerPhotoY={playerPhotoY}
                    playerPhotoScale={playerPhotoScale}
                    isPexelsBackground={!!(backgroundImage && !backgroundImage.startsWith('data:'))}
                    templateStyle={templateStyle}
                    gameContext={gameContext}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        <StatPickerModal
          isOpen={showStatPicker}
          onClose={() => setShowStatPicker(false)}
          onConfirm={(line, context) => { setStatLine(line); setGameContext(context || ''); }}
          games={games}
        />
    </>
  );
}
